import { useEffect, useRef } from 'react';

import { useOncePerUser } from '@/components/WhatsNewModal/useOncePerUser';
import useAuth from '@/hooks/useAuth';
import useOrderFlowV2Enabled from '@/hooks/useOrderFlowV2Enabled';
import { trackOrderV2FirstEntry } from '@/util/vexo';
import type { OrderType } from '@/util/vexo';

import useOrderFlowTour from '../hooks/useOrderFlowTour';

// One announcement = one key. Bump the suffix to re-run for everyone.
const ONBOARDING_KEY = 'grv:onboarding:order-v2-entry';
// Always-present entry anchor — the tour waits for it before starting so the
// driver.js overlay never opens against a not-yet-mounted screen.
const READY_ANCHOR = '[data-tour="orderflow-entry-billing"]';
// Cap the rAF wait so we give up rather than spin forever if the entry screen
// never renders (e.g. the user navigates away immediately).
const MAX_WAIT_FRAMES = 60;

type Props = {
  orderType: OrderType | null;
};

/**
 * First-entry onboarding for the order-v2 entry screen. Auto-runs the
 * image-annotated driver.js tour once per user (localStorage, keyed by user id)
 * and fires a vexo first-entry event. The "?" button keeps replaying the
 * text-only entry tour — this is the automatic first-run experience.
 *
 * Renders nothing: the tour is imperative (driver.js), so this component only
 * coordinates when to fire it.
 *
 * Inert for a client that is not on the OrderFlow V2 gate. The route guard
 * already redirects those clients before this mounts, but the check is repeated
 * here because this effect has side effects that outlive the tour: it burns the
 * once-per-user localStorage flag and emits `order_v2_first_entry`. Gating only
 * the tour itself would still consume the flag and skew the analytics for a user
 * who never saw the feature.
 */
const OrderV2EntryOnboarding = ({ orderType }: Props) => {
  const { currentAdmin } = useAuth();
  const orderFlowV2Enabled = useOrderFlowV2Enabled();
  const userId = currentAdmin?.user?.id;
  const storageKey = userId ? `${ONBOARDING_KEY}:${userId}` : null;

  const { open: firstTime, markSeen } = useOncePerUser(storageKey);
  const { startFirstEntryTour } = useOrderFlowTour();
  const firedRef = useRef(false);

  useEffect(() => {
    if (!orderFlowV2Enabled) return;
    if (!firstTime || firedRef.current) return;

    let frames = 0;
    let raf = 0;
    const tryStart = () => {
      if (firedRef.current) return;
      if (document.querySelector(READY_ANCHOR)) {
        firedRef.current = true;
        // Persist immediately so leaving without finishing still counts as
        // seen (true once-per-user), record the first entry, then drive.
        markSeen();
        trackOrderV2FirstEntry({ order_type: orderType ?? 'GIG' });
        startFirstEntryTour();
        return;
      }
      if (frames++ < MAX_WAIT_FRAMES) {
        raf = requestAnimationFrame(tryStart);
      }
    };
    raf = requestAnimationFrame(tryStart);

    return () => cancelAnimationFrame(raf);
  }, [orderFlowV2Enabled, firstTime, markSeen, orderType, startFirstEntryTour]);

  return null;
};

export default OrderV2EntryOnboarding;
